import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import {
  FiServer,
  FiWifi,
  FiWifiOff,
  FiActivity,
  FiMapPin,
  FiRefreshCw,
  FiCalendar,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
  FiUser,
  FiPackage,
  FiEdit2,
  FiChevronDown,
  FiSearch,
} from "react-icons/fi";
import "./DeviceMonitor.scss";
import { translate } from "../../../hooks/translate";
import DatePicker from "../../../components/CustomUI/DatePicker";
import Button from "../../../components/BaseUI/Button";
import Pagination from "../../../components/BaseUI/Pagination";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";

/** Display cap for product type in cards (keep in sync with nef-app `device-monitor.tsx`). */
const PRODUCT_TYPE_CHAR_LIMIT = 15;
const truncateWithEllipsis = (value, limit = PRODUCT_TYPE_CHAR_LIMIT) => {
  const str = (value ?? "").toString().replace(/\s+/g, " ").trim();
  if (!str) return "-";
  if (str.length <= limit) return str;
  const cut = Math.max(0, limit - 3);
  return `${str.slice(0, cut)}...`;
};

/** MQTT topic id from linked HardwareDevice (API may also expose computed machine.hardwareId) */
const getMqttHardwareIdForMachine = (machine) => {
  const fromApi = String(machine?.hardwareId || "").trim();
  if (fromApi) return fromApi;
  const hw = machine?.hardwareDeviceId;
  if (hw && typeof hw === "object") {
    return String(hw.hardwareId || hw.serialNumber || "").trim();
  }
  return "";
};

const DeviceMonitor = () => {
  const { getToken, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [partMachines, setPartMachines] = useState([]);
  const [productTypeEditId, setProductTypeEditId] = useState(null);
  const [productTypeEditMachine, setProductTypeEditMachine] = useState(null);
  const [productTypeDraftCodes, setProductTypeDraftCodes] = useState([]);
  const [savingProductType, setSavingProductType] = useState(false);
  const [productTypePage, setProductTypePage] = useState(1);
  const [inventoryMenuOpen, setInventoryMenuOpen] = useState(false);
  const [inventorySearchQuery, setInventorySearchQuery] = useState("");
  const inventoryDropdownRef = useRef(null);
  const [lastActivityByMachine, setLastActivityByMachine] = useState({});
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [periodView, setPeriodView] = useState("day"); // 'day', 'week', 'month', 'custom'
  const [selectedDate, setSelectedDate] = useState(new Date()); // Selected date for day view
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [tempDateRange, setTempDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isIntentionalCloseRef = useRef(false);
  const mountedRef = useRef(true);
  const tokenRef = useRef(getToken());
  const maxReconnectAttempts = 5;
  const offlineCheckIntervalRef = useRef(null);
  const OFFLINE_TIMEOUT = 120000; // 2 minutes in milliseconds

  // Format time since
  const formatTimeSince = (date) => {
    if (!date) return translate("UI.NEVER");

    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    if (seconds < 60) return `${seconds}sn önce`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}dk önce`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}sa önce`;
    return `${Math.floor(seconds / 86400)}g önce`;
  };

  // Normalize to start of day (local) for consistent comparison
  const toDayStart = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const toDayEnd = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.getTime();
  };

  // Get count for a specific period (day/week/month/custom)
  const getPeriodCount = (machine, period) => {
    if (!machine.openingCounts || !Array.isArray(machine.openingCounts)) {
      if (period === "day") {
        const sel = new Date(selectedDate);
        const today = new Date();
        if (
          sel.getFullYear() === today.getFullYear() &&
          sel.getMonth() === today.getMonth() &&
          sel.getDate() === today.getDate()
        ) {
          return machine.openedCountToday ?? 0;
        }
        return 0;
      }
      return 0;
    }

    const now = new Date();
    let startDate;
    let endDate;

    if (period === "day") {
      const dayDate = new Date(selectedDate);
      startDate = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        0,
        0,
        0,
        0,
      );
      endDate = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        23,
        59,
        59,
        999,
      );
    } else if (period === "week") {
      const dayOfWeek = now.getDay();
      startDate = new Date(now);
      startDate.setDate(now.getDate() - dayOfWeek);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
    } else if (period === "month") {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      endDate = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );
    } else if (period === "custom") {
      if (!customDateRange.startDate || !customDateRange.endDate) {
        return 0;
      }
      const start = new Date(customDateRange.startDate);
      const end = new Date(customDateRange.endDate);
      startDate = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        0,
        0,
        0,
        0,
      );
      endDate = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        23,
        59,
        59,
        999,
      );
      if (startDate.getTime() > endDate.getTime()) return 0;
    } else {
      return 0;
    }

    const startTs = toDayStart(startDate);
    const endTs = toDayEnd(endDate);

    return machine.openingCounts.reduce((total, entry) => {
      const entryTs = toDayStart(entry.date);
      if (entryTs >= startTs && entryTs <= endTs) {
        return total + (entry.count || 0);
      }
      return total;
    }, 0);
  };

  const getPeriodProductType = (machine, period) => {
    const collect = (entries) => {
      const values = [];
      for (const e of entries) {
        if (e.productTypes?.length) values.push(...e.productTypes);
        else if (e.productType) values.push(e.productType);
      }
      return [...new Set(values.filter(Boolean))];
    };

    if (!machine.openingCounts?.length) {
      const fromTop = collect(
        machine.productTypes ? [{ productTypes: machine.productTypes }] : [],
      );
      if (machine.productTypesDetail?.length) {
        return machine.productTypesDetail.map((d) => d.code || d.name).join(", ");
      }
      if (machine.productTypeCode) return machine.productTypeCode;
      if (fromTop.length) return fromTop.join(", ");
      return "-";
    }

    const now = new Date();
    let startDate;
    let endDate;

    if (period === "day") {
      const dayDate = new Date(selectedDate);
      startDate = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        0,
        0,
        0,
        0,
      );
      endDate = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        23,
        59,
        59,
        999,
      );
    } else if (period === "week") {
      const dayOfWeek = now.getDay();
      startDate = new Date(now);
      startDate.setDate(now.getDate() - dayOfWeek);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
    } else if (period === "month") {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      endDate = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );
    } else if (period === "custom") {
      if (!customDateRange.startDate || !customDateRange.endDate) return "-";
      const start = new Date(customDateRange.startDate);
      const end = new Date(customDateRange.endDate);
      startDate = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        0,
        0,
        0,
        0,
      );
      endDate = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        23,
        59,
        59,
        999,
      );
    } else {
      return "-";
    }

    const startTs = toDayStart(startDate);
    const endTs = toDayEnd(endDate);

    const inRange = machine.openingCounts.filter((e) => {
      if (!e?.date) return false;
      const ts = toDayStart(e.date);
      return ts >= startTs && ts <= endTs;
    });

    const values = collect(inRange);
    const labelOf = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return "";
      const detail = (machine.productTypesDetail || []).find(
        (d) => String(d.id) === s || String(d.code) === s,
      );
      if (detail) return detail.code || detail.name || s;
      return machine.productTypeCode && String(machine.productType) === s
        ? machine.productTypeCode
        : s;
    };
    if (values.length) return values.map(labelOf).join(", ");
    if (machine.productTypeCode) return machine.productTypeCode;
    if (machine.productTypesDetail?.length) {
      return machine.productTypesDetail.map((d) => d.code || d.name).join(", ");
    }
    return "-";
  };

  const inventoryItemMachineId = (it) => {
    const m = it?.machineId;
    return m?._id?.toString?.() ?? m?._id ?? m ?? "";
  };

  /** Inventory outputs tied to this machine (InventoryItem.machineId). */
  const inventoryProducedByMachine = (items, machine) => {
    const mid = machine?._id?.toString?.() ?? machine?._id ?? "";
    if (!mid) return [];
    return items
      .filter((it) => String(inventoryItemMachineId(it)) === String(mid))
      .map((it) => {
        const part = it?.partId && typeof it.partId === "object" ? it.partId : null;
        const partId = String(part?._id || it?.partId || "").trim();
        const code = String(part?.code || it?.code || "").trim();
        const name = String(part?.name || it?.name || "").trim();
        if (!partId || !code) return null;
        return { _id: partId, partId, code, name };
      })
      .filter(Boolean);
  };

  /** Parts that this machine is allowed to produce (from PartMachine). */
  const producedPartsByMachine = useCallback(
    (machine) => {
      const mid = machine?._id?.toString?.() ?? machine?._id ?? "";
      if (!mid) return [];
      const items = (partMachines || [])
        .filter((pm) => {
          const m = pm?.machine;
          const pmMid = m?._id?.toString?.() ?? m?._id ?? m ?? "";
          return String(pmMid) === String(mid) && pm?.isActive !== false;
        })
        .map((pm) => pm?.part)
        .filter((p) => p && p.code)
        .map((p) => ({
          _id: p._id,
          partId: p._id,
          code: p.code,
          name: p.name,
        }));
      // Dedup by code
      const map = new Map();
      items.forEach((it) => {
        const k = String(it.code || "").trim();
        if (!k) return;
        if (!map.has(k)) map.set(k, it);
      });
      return Array.from(map.values()).sort((a, b) =>
        String(a.code || "").localeCompare(String(b.code || "")),
      );
    },
    [partMachines],
  );

  // Navigate to previous day
  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  // Navigate to next day
  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    // Don't allow future dates
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (newDate <= today) {
      setSelectedDate(newDate);
    }
  };

  // Convert DD/MM/YYYY to Date object
  const parseDateFromPicker = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("/");
    return new Date(year, month - 1, day);
  };

  // Convert Date object to DD/MM/YYYY
  const formatDateForPicker = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle custom date range selection
  const handleCustomPeriodSelect = () => {
    // Initialize temp dates with current custom range if exists
    setTempDateRange({
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    });
    setShowDatePicker(true);
  };

  const handleStartDateChange = (dateString) => {
    const date = parseDateFromPicker(dateString);
    setTempDateRange((prev) => ({
      ...prev,
      startDate: date,
    }));
  };

  const handleEndDateChange = (dateString) => {
    const date = parseDateFromPicker(dateString);
    setTempDateRange((prev) => ({
      ...prev,
      endDate: date,
    }));
  };

  const applyDateRange = () => {
    if (tempDateRange.startDate && tempDateRange.endDate) {
      setCustomDateRange({
        startDate: tempDateRange.startDate,
        endDate: tempDateRange.endDate,
      });
      setPeriodView("custom");
      setShowDatePicker(false);
    }
  };

  const clearCustomDateRange = () => {
    setCustomDateRange({ startDate: null, endDate: null });
    setTempDateRange({ startDate: null, endDate: null });
    setPeriodView("day");
    setShowDatePicker(false);
  };

  const cancelDateRange = () => {
    setTempDateRange({
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    });
    if (!customDateRange.startDate || !customDateRange.endDate) {
      setPeriodView("day");
    }
    setShowDatePicker(false);
  };

  // Fetch last activity per machine (one activity per machine, newest first)
  const fetchLastActivityByMachine = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/activities`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, limit: 500 },
        },
      );
      const list = res.data?.data?.activities || [];
      const byMachine = {};
      list.forEach((a) => {
        const mid = a.machine?.id || a.machine?._id;
        const key = mid?.toString?.() ?? mid;
        if (key && !byMachine[key]) {
          byMachine[key] = {
            user: a.user,
            timestamp: a.timestamp,
          };
        }
      });
      setLastActivityByMachine(byMachine);
    } catch (e) {
      console.error("Error fetching last activity by machine:", e);
    }
  }, [getToken]);

  // Fetch machines from API
  const fetchMachines = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/machines`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      console.log(response.data);
      if (response.data.machines) {
        console.log(response.data.machines);
        setMachines(
          response.data.machines.filter((m) => m?.isActive !== false),
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching machines:", error);
      setLoading(false);
    }
  }, [getToken]);

  const machineProductTypeIds = (m) => {
    const fromArr = (m?.productTypes || [])
      .map((c) => String(c || "").trim())
      .filter(Boolean);
    if (fromArr.length) return fromArr;
    const single = String(m?.productType || m?.product_type || "").trim();
    return single && /^[a-fA-F0-9]{24}$/i.test(single) ? [single] : [];
  };

  const partOptionId = (it) => {
    const raw = it?.partId?._id || it?.partId || it?._id || "";
    const s = String(raw || "").trim();
    if (/^[a-fA-F0-9]{24}$/i.test(s)) return s.toLowerCase();
    return "";
  };

  const normalizeDraftToPartIds = (draft, options) => {
    const out = [];
    const seen = new Set();
    for (const raw of draft || []) {
      const s = String(raw || "").trim();
      if (!s) continue;
      let id = /^[a-fA-F0-9]{24}$/i.test(s) ? s.toLowerCase() : "";
      if (!id) {
        const byCode = options.find(
          (it) => String(it.code || "").toLowerCase() === s.toLowerCase(),
        );
        id = partOptionId(byCode);
      }
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out.slice(0, 2);
  };

  const toggleProductTypeDraft = (partId) => {
    const id = String(partId || "").trim().toLowerCase();
    if (!id) return;
    setProductTypeDraftCodes((prev) => {
      const has = prev.some((x) => x === id);
      if (has) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev;
      return [...prev, id];
    });
  };

  const cancelProductTypeEdit = () => {
    setInventoryMenuOpen(false);
    setInventorySearchQuery("");
    setProductTypeEditId(null);
    setProductTypeEditMachine(null);
    setProductTypeDraftCodes([]);
    setProductTypePage(1);
  };

  const openProductTypeEdit = async (machine) => {
    if (!isAdmin) return;
    const id = machine._id?.toString?.() ?? machine._id;
    setInventoryMenuOpen(false);
    setInventorySearchQuery("");
    setProductTypeEditId(id);
    setProductTypeEditMachine(machine ?? null);
    setProductTypePage(1);

    let nextPartMachines = partMachines;
    let nextInventory = inventoryItems;

    if (!nextPartMachines.length) {
      try {
        const token = getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/part-machines`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        nextPartMachines = Array.isArray(res.data?.partMachines)
          ? res.data.partMachines
          : [];
        setPartMachines(nextPartMachines);
      } catch (e) {
        console.error("Error fetching part-machines:", e);
      }
    }

    if (!nextInventory.length) {
      try {
        const token = getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/inventory`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        nextInventory = res.data?.items || [];
        setInventoryItems(nextInventory);
      } catch (e) {
        console.error("Error fetching inventory:", e);
      }
    }

    const mid = machine?._id?.toString?.() ?? machine?._id ?? "";
    const pmParts = (nextPartMachines || [])
      .filter((pm) => {
        const m = pm?.machine;
        const pmMid = m?._id?.toString?.() ?? m?._id ?? m ?? "";
        return String(pmMid) === String(mid) && pm?.isActive !== false;
      })
      .map((pm) => pm?.part)
      .filter((p) => p && p.code)
      .map((p) => ({
        _id: p._id,
        partId: p._id,
        code: p.code,
        name: p.name,
      }));
    const options =
      pmParts.length > 0
        ? pmParts
        : inventoryProducedByMachine(nextInventory, machine);

    const configured = machineProductTypeIds(machine);
    const draft = configured.length
      ? normalizeDraftToPartIds(configured, options)
      : [];
    setProductTypeDraftCodes(draft);
  };

  const saveMachineProductType = async (machine) => {
    const mid = machine._id?.toString?.() ?? machine._id;
    const options =
      producedPartsByMachine(machine).length > 0
        ? producedPartsByMachine(machine)
        : inventoryProducedByMachine(inventoryItems, machine);
    const ids = normalizeDraftToPartIds(productTypeDraftCodes, options);
    if (productTypeDraftCodes.length && !ids.length) {
      console.error("Failed to resolve selected parts to Part ids");
      return;
    }
    setSavingProductType(true);
    try {
      const token = getToken();
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/machine-live/${mid}/product-types`,
        {
          productTypes: ids,
          productType: ids[0] || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const saved = res.data?.machine || {};
      setMachines((prev) =>
        prev.map((m) => {
          const mId = m._id?.toString?.() ?? m._id;
          if (mId !== mid) return m;
          return {
            ...m,
            ...saved,
            productTypes: saved.productTypes || ids,
            productType: saved.productType || ids[0] || null,
            productTypesDetail: saved.productTypesDetail || m.productTypesDetail,
            productTypeCode: saved.productTypeCode || m.productTypeCode,
            productName: saved.productName || m.productName,
          };
        }),
      );
      cancelProductTypeEdit();
    } catch (e) {
      console.error("Error saving product type:", e);
    } finally {
      setSavingProductType(false);
    }
  };

  useEffect(() => {
    if (!inventoryMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (
        inventoryDropdownRef.current &&
        !inventoryDropdownRef.current.contains(e.target)
      ) {
        setInventoryMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setInventoryMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [inventoryMenuOpen]);

  useEffect(() => {
    if (!inventoryMenuOpen) setInventorySearchQuery("");
  }, [inventoryMenuOpen]);

  // Cleanup WebSocket connection
  const cleanupWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const ws = wsRef.current;
    if (ws) {
      wsRef.current = null;
      isIntentionalCloseRef.current = true;

      try {
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close(1000, "Cleanup");
        }
      } catch (error) {
        console.error("[WebSocket] Error closing connection:", error);
      }
    }
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(
    (token) => {
      if (!mountedRef.current) return;

      try {
        const apiUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
        let wsUrl = apiUrl.replace(/^http/, "ws");

        if (!wsUrl.startsWith("ws://") && !wsUrl.startsWith("wss://")) {
          wsUrl = `ws://${wsUrl.replace(/^https?:\/\//, "")}`;
        }

        const wsPath = wsUrl.endsWith("/") ? "ws" : "/ws";
        const ws = new WebSocket(`${wsUrl}${wsPath}?token=${token}`);

        wsRef.current = ws;
        isIntentionalCloseRef.current = false;
        setWsConnected(false);

        let isConnectionEstablished = false;
        let connectionTimeout = setTimeout(() => {
          if (!isConnectionEstablished && ws.readyState !== WebSocket.OPEN) {
            ws.close(1000, "Connection timeout");
          }
        }, 10000);

        // Message verification function
        const verifyMessage = (data) => {
          try {
            const messageData =
              typeof data === "string" ? JSON.parse(data) : data;

            if (!messageData.type || !messageData.timestamp) {
              return null;
            }

            // Time check (reject messages older than 5 minutes)
            const messageAge = Date.now() - messageData.timestamp;
            if (messageAge > 300000) {
              return null;
            }

            return messageData;
          } catch (error) {
            return null;
          }
        };

        ws.onopen = () => {
          if (!mountedRef.current) {
            ws.close(1000, "Component unmounted");
            return;
          }

          clearTimeout(connectionTimeout);
          console.log("[WebSocket] Connected");
          setWsConnected(true);
          reconnectAttemptsRef.current = 0;
          isConnectionEstablished = true;
        };

        ws.onmessage = (event) => {
          if (!mountedRef.current) return;

          try {
            let messageData = JSON.parse(event.data);

            if (wsRef.current !== ws) return;

            // Verify message
            const verifiedData = verifyMessage(messageData);
            if (!verifiedData) return;
            messageData = verifiedData;

            if (messageData.type === "telemetry") {
              if (messageData.deviceId === "machine_update") {
                // Full machine list update (from periodic checks or resets)
                if (messageData.data?.machines) {
                  setMachines(
                    messageData.data.machines.filter((m) => m?.isActive !== false),
                  );
                }
              } else {
                // Individual machine update (from status/telemetry)
                const { deviceId, data } = messageData;
                setMachines((prevMachines) => {
                  const machineIndex = prevMachines.findIndex((m) => {
                    const mqttId = getMqttHardwareIdForMachine(m);
                    return mqttId === deviceId || m.code === deviceId;
                  });
                  if (machineIndex === -1) return prevMachines;

                  const updatedMachines = [...prevMachines];
                  const updatedMachine = {
                    ...updatedMachines[machineIndex],
                  };

                  // Update status if provided
                  if (data.status !== undefined && data.status !== null) {
                    updatedMachine.status = data.status;
                  }

                  // Update lastHeartbeat if provided
                  if (
                    data.lastHeartbeat !== undefined &&
                    data.lastHeartbeat !== null
                  ) {
                    updatedMachine.lastHeartbeat = data.lastHeartbeat;
                  }

                  updatedMachines[machineIndex] = updatedMachine;
                  return updatedMachines;
                });
              }
            } else if (messageData.type === "ping") {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(
                  JSON.stringify({ type: "pong", timestamp: Date.now() }),
                );
              }
            }
          } catch (error) {
            console.error("[WebSocket] Error parsing message:", error);
          }
        };

        ws.onerror = (error) => {
          console.error("[WebSocket] Error:", error);
        };

        ws.onclose = () => {
          clearTimeout(connectionTimeout);

          if (!mountedRef.current) return;

          const wasIntentional = isIntentionalCloseRef.current;

          if (wsRef.current === ws) {
            wsRef.current = null;
          }

          console.log("[WebSocket] Disconnected");
          setWsConnected(false);

          // Reconnect with exponential backoff
          if (
            !wasIntentional &&
            mountedRef.current &&
            isConnectionEstablished &&
            reconnectAttemptsRef.current < maxReconnectAttempts
          ) {
            reconnectAttemptsRef.current++;
            const delay = Math.min(
              1000 * Math.pow(2, reconnectAttemptsRef.current),
              30000,
            );

            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }

            reconnectTimeoutRef.current = setTimeout(() => {
              if (
                mountedRef.current &&
                (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)
              ) {
                initializeWebSocket();
              }
            }, delay);
          }
        };
      } catch (error) {
        console.error("[WebSocket] Connection error:", error);
      }
    },
    [maxReconnectAttempts],
  );

  // Initialize WebSocket
  const initializeWebSocket = useCallback(() => {
    if (!mountedRef.current) return;

    const currentToken = tokenRef.current;
    if (!currentToken) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    if (wsRef.current) {
      cleanupWebSocket();
      setTimeout(() => {
        connectWebSocket(currentToken);
      }, 1000);
      return;
    }

    connectWebSocket(currentToken);
  }, [cleanupWebSocket, connectWebSocket]);

  // Offline check - runs every 30 seconds
  useEffect(() => {
    const checkOfflineMachines = () => {
      setMachines((prevMachines) => {
        const now = new Date();
        const updatedMachines = prevMachines.map((machine) => {
          // Only check machines that are currently online
          if (machine.status === "online" && machine.lastHeartbeat) {
            const lastHeartbeat = new Date(machine.lastHeartbeat);
            const timeSinceLastHeartbeat =
              now.getTime() - lastHeartbeat.getTime();

            // If last heartbeat is older than 2 minutes, mark as offline
            if (timeSinceLastHeartbeat > OFFLINE_TIMEOUT) {
              return {
                ...machine,
                status: "offline",
              };
            }
          }
          return machine;
        });

        // Only update if something changed
        const hasChanges = updatedMachines.some(
          (m, index) => m.status !== prevMachines[index]?.status,
        );

        return hasChanges ? updatedMachines : prevMachines;
      });
    };

    // Check every 30 seconds
    offlineCheckIntervalRef.current = setInterval(checkOfflineMachines, 30000);

    return () => {
      if (offlineCheckIntervalRef.current) {
        clearInterval(offlineCheckIntervalRef.current);
      }
    };
  }, []);

  // Component mount/unmount tracking
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initial load
  useEffect(() => {
    fetchMachines();
    fetchLastActivityByMachine();
    initializeWebSocket();

    return () => {
      cleanupWebSocket();
      if (offlineCheckIntervalRef.current) {
        clearInterval(offlineCheckIntervalRef.current);
      }
    };
  }, [
    fetchMachines,
    fetchLastActivityByMachine,
    initializeWebSocket,
    cleanupWebSocket,
  ]);

  // Request telemetry for a specific machine
  const requestMachineTelemetry = async (machine) => {
    try {
      const token = getToken();
      const hardwareId = getMqttHardwareIdForMachine(machine);
      if (!hardwareId) {
        console.error("MQTT hardware id missing for machine", machine?._id);
        return;
      }
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/machines/${encodeURIComponent(hardwareId)}/command`,
        { action: "get_telemetry" },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error("Error requesting telemetry:", error);
    }
  };

  // Manual refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Request telemetry for all machines
      const token = getToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/machines/telemetry/all`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error("Error requesting telemetry for all machines:", error);
    }

    // Fetch updated machine data and last activities
    await fetchMachines();
    await fetchLastActivityByMachine();
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="device-monitor">
        <div className="loading-spinner">
          <FiRefreshCw className="spin" size={48} />
          <p>{translate("UI.LOADING_MACHINES")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="device-monitor">
      <div className="monitor-header">
        <div className="header-left">
          <h1>
            <FiServer /> {translate("UI.PRODUCTION_MONITOR")}
          </h1>
          <div className="connection-status">
            <div
              className={`status-indicator ${
                wsConnected ? "online" : "offline"
              }`}
            />
            <span>
              {wsConnected
                ? translate("UI.LIVE")
                : translate("UI.DISCONNECTED")}
            </span>
          </div>
        </div>
        <div className="header-right">
          {periodView === "custom" &&
            customDateRange.startDate &&
            customDateRange.endDate && (
              <div className="custom-date-badge">
                <span>
                  {formatDateForPicker(customDateRange.startDate)} -{" "}
                  {formatDateForPicker(customDateRange.endDate)}
                </span>
                <button
                  className="clear-badge-btn"
                  onClick={clearCustomDateRange}
                  title={translate("UI.CLEAR_DATE_RANGE")}
                >
                  <FiX size={12} />
                </button>
              </div>
            )}
          <div className="period-selector">
            {periodView === "day" && (
              <div className="day-navigation">
                <button
                  className="nav-day-btn"
                  onClick={handlePreviousDay}
                  title={translate("UI.PREVIOUS_DAY")}
                >
                  <FiChevronLeft size={16} />
                </button>
                <span className="selected-date-label">
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const selected = new Date(selectedDate);
                    selected.setHours(0, 0, 0, 0);
                    if (selected.getTime() === today.getTime()) {
                      return translate("UI.TODAY");
                    }
                    return formatDateForPicker(selectedDate);
                  })()}
                </span>
                <button
                  className="nav-day-btn"
                  onClick={handleNextDay}
                  disabled={
                    new Date(selectedDate).toDateString() ===
                    new Date().toDateString()
                  }
                  title={translate("UI.NEXT_DAY")}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            )}
            <button
              className={`period-btn ${periodView === "day" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("day");
                setShowDatePicker(false);
              }}
              title={translate("UI.DAY")}
            >
              {translate("UI.DAY")}
            </button>
            <button
              className={`period-btn ${periodView === "week" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("week");
                setShowDatePicker(false);
              }}
              title={translate("UI.THIS_WEEK")}
            >
              {translate("UI.WEEK")}
            </button>
            <button
              className={`period-btn ${periodView === "month" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("month");
                setShowDatePicker(false);
              }}
              title={translate("UI.THIS_MONTH")}
            >
              {translate("UI.MONTH")}
            </button>
            <button
              className={`period-btn ${
                periodView === "custom" ? "active" : ""
              }`}
              onClick={handleCustomPeriodSelect}
              title={translate("UI.CUSTOM_DATE_RANGE")}
            >
              <FiCalendar size={14} /> {translate("UI.CUSTOM")}
            </button>
          </div>
          {showDatePicker && (
            <div className="custom-date-range-picker">
              <div className="date-range-header">
                <span>{translate("UI.SELECT_DATE_RANGE")}</span>
                <button className="close-date-picker" onClick={cancelDateRange}>
                  <FiX size={16} />
                </button>
              </div>
              <div className="date-range-inputs">
                <div className="date-input-group">
                  <DatePicker
                    label={translate("UI.START_DATE")}
                    placeholder={translate("UI.SELECT_START_DATE")}
                    value={
                      tempDateRange.startDate
                        ? formatDateForPicker(tempDateRange.startDate)
                        : ""
                    }
                    maxDate={
                      tempDateRange.endDate
                        ? formatDateForPicker(tempDateRange.endDate)
                        : formatDateForPicker(new Date())
                    }
                    onChange={handleStartDateChange}
                  />
                </div>
                <div className="date-input-group">
                  <DatePicker
                    label={translate("UI.END_DATE")}
                    placeholder={translate("UI.SELECT_END_DATE")}
                    value={
                      tempDateRange.endDate
                        ? formatDateForPicker(tempDateRange.endDate)
                        : ""
                    }
                    minDate={
                      tempDateRange.startDate
                        ? formatDateForPicker(tempDateRange.startDate)
                        : undefined
                    }
                    maxDate={formatDateForPicker(new Date())}
                    onChange={handleEndDateChange}
                  />
                </div>
                <div className="date-range-actions">
                  <button
                    className="apply-date-range-btn"
                    onClick={applyDateRange}
                    disabled={
                      !tempDateRange.startDate || !tempDateRange.endDate
                    }
                    title={translate("UI.APPLY_DATE_RANGE")}
                  >
                    {translate("UI.APPLY")}
                  </button>
                  {(tempDateRange.startDate || tempDateRange.endDate) && (
                    <button
                      className="clear-date-range-btn"
                      onClick={clearCustomDateRange}
                      title={translate("UI.CLEAR_DATE_RANGE")}
                    >
                      <FiX size={14} /> {translate("UI.CLEAR")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          <Button
            appearance="primary-animated"
            iconBefore={<FiRefreshCw />}
            onClick={handleRefresh}
          >
            {" "}
            {translate("UI.REFRESH")}
          </Button>
        </div>
      </div>
      <div className="machines-grid">
        {machines.map((machine) => {
          return (
            <div
              key={machine._id}
              className={`machine-card ${machine.status || "offline"}`}
            >
              <div className="card-header">
                <div className="machine-name">
                  <FiServer size={20} />
                  <h3>{machine.name}</h3>
                </div>
                <div className="header-actions">
                  {/* <button
              className="telemetry-btn"
              onClick={() => requestMachineTelemetry(machine)}
              title={translate("UI.REQUEST_TELEMETRY")}
            >
              <FiRadio size={16} />
            </button> */}
                  <div
                    className={`status-badge ${machine.status || "offline"}`}
                  >
                    {machine.status === "online" ? (
                      <>
                        <FiWifi />
                      </>
                    ) : (
                      <>
                        <FiWifiOff />
                      </>
                    )}
                  </div>
                  <button
                    className="view-activity-mini-btn"
                    onClick={() =>
                      navigate(`/activities/machine/${machine._id}`)
                    }
                    title={
                      translate("UI.VIEW_MACHINE_ACTIVITIES") ||
                      "View Machine Activities"
                    }
                  >
                    <FiExternalLink size={14} />
                  </button>
                </div>
              </div>

              <div className="card-body">
                {machine.location && (
                  <div className="info-row">
                    <FiMapPin size={14} />
                    <span className="value">{machine.location}</span>
                  </div>
                )}
                {(() => {
                  const lastAct =
                    lastActivityByMachine[
                      machine._id?.toString?.() ?? machine._id
                    ];
                  return (
                    <div className="info-row last-activity-row">
                      <FiUser size={14} />
                      <span className="value">
                        {translate("UI.LAST_ACTIVITY_BY")}:{" "}
                        <strong>
                          {lastAct
                            ? lastAct.user?.name || lastAct.user?.email || "-"
                            : "-"}
                        </strong>{" "}
                        {lastAct
                          ? `(${formatTimeSince(lastAct.timestamp)})`
                          : "-"}
                      </span>
                    </div>
                  );
                })()}

                <div className="divider" />

                <div className="live-data">
                  <div className="data-item">
                    <div className="data-icon">
                      <FiActivity />
                    </div>
                    <div className="data-content">
                      <span className="data-label">
                        {translate("UI.START_TIMES")}
                      </span>
                      <span className="data-value">
                        {getPeriodCount(machine, periodView)}
                      </span>
                    </div>
                  </div>
                  <div className="data-item">
                    <div className="data-icon">
                      <FiPackage />
                    </div>
                    <div
                      className="data-content"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <span className="data-label">
                        {translate("UI.PRODUCT_TYPE")}
                      </span>
                      {(() => {
                        const fullValue = getPeriodProductType(
                          machine,
                          periodView,
                        );
                        const truncated = truncateWithEllipsis(fullValue);
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              className="data-value"
                              style={{ fontSize: 14, lineHeight: "16px" }}
                              title={
                                typeof fullValue === "string" &&
                                fullValue.trim() &&
                                fullValue !== "-"
                                  ? fullValue
                                  : undefined
                              }
                            >
                              {truncated}
                            </span>
                            {isAdmin && (
                              <button
                                type="button"
                                className="device-monitor-product-type-edit"
                                title={
                                  translate("UI.EDIT") || "Edit product type"
                                }
                                onClick={() => openProductTypeEdit(machine)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 4,
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "inherit",
                                  opacity: 0.85,
                                }}
                              >
                                <FiEdit2 size={15} />
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {machine.notes && (
                <div className="card-footer">
                  <small>{machine.notes}</small>
                </div>
              )}
            </div>
          );
        })}

        {machines.length === 0 && (
          <div className="no-machines">
            <FiServer size={64} />
            <h3>{translate("UI.NO_MACHINES_FOUND")}</h3>
            <p>{translate("UI.ADD_MACHINES_TO_START_MONITORING")}</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={Boolean(productTypeEditId && productTypeEditMachine)}
        width="medium"
        scrollBehavior="inside"
        onClose={savingProductType ? undefined : cancelProductTypeEdit}
        className="device-monitor-product-type-modal"
      >
        <ModalHeader>
          {translate("UI.PRODUCT_TYPE")} {translate("UI.EDIT") || "Edit"}
        </ModalHeader>
        <ModalBody className="device-monitor-product-type-modal__body">
          {(() => {
            const machine = productTypeEditMachine;
            if (!machine) return null;

            const PAGE_SIZE = 10;
            const invQ = inventorySearchQuery.trim().toLowerCase();

            const machineInventoryItems =
              producedPartsByMachine(machine).length > 0
                ? producedPartsByMachine(machine)
                : inventoryProducedByMachine(inventoryItems, machine);
            const filteredInventoryItems = invQ
              ? machineInventoryItems.filter((it) => {
                  const c = String(it.code || "").toLowerCase();
                  const n = String(it.name || "").toLowerCase();
                  return c.includes(invQ) || n.includes(invQ);
                })
              : machineInventoryItems;

            const totalItems = filteredInventoryItems.length;
            const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
            const safePage = Math.min(Math.max(1, productTypePage), pageCount);
            const startIdx = (safePage - 1) * PAGE_SIZE;
            const endIdx = startIdx + PAGE_SIZE;

            const rows = filteredInventoryItems.slice(startIdx, endIdx);

            const selectedLabel =
              productTypeDraftCodes.length > 0
                ? productTypeDraftCodes
                    .map((id) => {
                      const hit = machineInventoryItems.find(
                        (it) => partOptionId(it) === String(id),
                      );
                      return hit?.code || hit?.name || id;
                    })
                    .join(" / ")
                : translate("UI.SELECT") || "—";

            return (
              <div className="device-monitor-product-type-list">
                <div className="device-monitor-product-type-list__search">
                  <FiSearch
                    size={16}
                    aria-hidden
                    className="device-monitor-product-type-list__search-icon"
                  />
                  <input
                    type="search"
                    className="device-monitor-product-type-list__search-input"
                    value={inventorySearchQuery}
                    onChange={(e) => {
                      setInventorySearchQuery(e.target.value);
                      setProductTypePage(1);
                    }}
                    placeholder={
                      translate("UI.SEARCH") ||
                      translate("UI.SEARCH_DEVICES") ||
                      "Search…"
                    }
                    aria-label={translate("UI.SEARCH") || "Search"}
                    autoComplete="off"
                    autoFocus
                    disabled={savingProductType}
                  />
                </div>

                <div
                  className="device-monitor-product-type-list__items"
                  role="listbox"
                  aria-label={translate("UI.PRODUCT_TYPE")}
                >
                  {rows.length === 0 ? (
                    <div className="device-monitor-product-type-list__empty">
                      {translate("UI.NO_RESULTS") || "No matching items"}
                    </div>
                  ) : (
                    rows.map((it) => {
                      const id = partOptionId(it);
                      const isSelected = productTypeDraftCodes.some(
                        (x) => String(x).toLowerCase() === String(id).toLowerCase(),
                      );
                      const atMax =
                        productTypeDraftCodes.length >= 2 && !isSelected;
                      const optLabel = [it.code, it.name]
                        .filter(Boolean)
                        .join(" — ");
                      return (
                        <button
                          key={id || it._id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          title={optLabel}
                          disabled={savingProductType || atMax || !id}
                          className={`device-monitor-product-type-list__item${
                            isSelected ? " is-selected" : ""
                          }`}
                          onClick={() => toggleProductTypeDraft(id)}
                        >
                          <span className="device-monitor-product-type-list__code">
                            {it.code}
                          </span>
                          {it.name ? (
                            <span className="device-monitor-product-type-list__name">
                              {it.name}
                            </span>
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>

                <Pagination
                  totalItems={totalItems}
                  totalPerPage={PAGE_SIZE}
                  currentPage={safePage}
                  options={{
                    selectQuantity: false,
                    simpleCounting: true,
                    skipPrevious: true,
                    skipNext: true,
                    previous: true,
                    next: true,
                  }}
                  onPageChange={({ pagination }) => {
                    setProductTypePage(pagination?.currentPage || 1);
                  }}
                />
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            disabled={savingProductType}
            onClick={cancelProductTypeEdit}
          >
            {translate("UI.CANCEL")}
          </Button>
          <Button
            appearance="primary-animated"
            type="button"
            disabled={savingProductType || !productTypeEditMachine}
            onClick={() =>
              productTypeEditMachine
                ? saveMachineProductType(productTypeEditMachine)
                : undefined
            }
          >
            {translate("UI.SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DeviceMonitor;
